import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle } from 'lucide-react';
import { useTutorStore } from '@shared/state';
import { trackEvent, getBaseProperties } from '@shared/services/analytics';
import { ANALYTICS_EVENTS } from '@shared/constants';

interface TextHighlightTriggerProps {
  containerRef: React.RefObject<HTMLElement | null>;
}

export function TextHighlightTrigger({ containerRef }: TextHighlightTriggerProps) {
  const openPanel = useTutorStore((s) => s.openPanel);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; text: string } | null>(null);

  const handleSelectionChange = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || !selection.toString().trim()) {
      setTooltip(null);
      return;
    }

    // Check if selection is within our container
    const container = containerRef.current;
    if (!container) return;

    const anchorNode = selection.anchorNode;
    if (!anchorNode || !container.contains(anchorNode)) {
      setTooltip(null);
      return;
    }

    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    const text = selection.toString().trim();

    if (text.length < 5 || text.length > 500) {
      setTooltip(null);
      return;
    }

    setTooltip({
      x: rect.left + rect.width / 2,
      y: rect.top - 8,
      text,
    });
  }, [containerRef]);

  useEffect(() => {
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, [handleSelectionChange]);

  function handleAskTutor() {
    if (!tooltip) return;

    trackEvent({
      name: ANALYTICS_EVENTS.TUTOR_HIGHLIGHT_CONTEXT,
      properties: {
        ...getBaseProperties(),
        nodeId: '',
        conceptId: null,
        highlightLength: tooltip.text.length,
      },
    }).catch(() => {});

    openPanel({ highlightedText: tooltip.text });
    setTooltip(null);
    window.getSelection()?.removeAllRanges();
  }

  return (
    <AnimatePresence>
      {tooltip && (
        <motion.button
          initial={{ opacity: 0, y: 4, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 4, scale: 0.9 }}
          transition={{ duration: 0.15 }}
          onClick={handleAskTutor}
          className="fixed z-[60] flex items-center gap-1.5 px-3 py-1.5 rounded-pill
            bg-indigo-500 text-white text-xs font-body font-semibold
            shadow-glow-indigo cursor-pointer hover:bg-[#7E70EF] transition-colors"
          style={{
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <MessageCircle size={12} />
          Ask Tutor
        </motion.button>
      )}
    </AnimatePresence>
  );
}
